import "@/components/global/components/tiptap/tiptap-content.css";
import "@/components/global/components/tiptap/tiptap-toolbar.css";

import { Box } from "@mui/material";
import type { EditorView } from "@tiptap/pm/view";
import { EditorContent } from "@tiptap/react";
import {
	forwardRef,
	type MouseEvent,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";

import { SAMPLE_RESOURCES } from "@/components/global/components/tiptap/domain/resources";
import EditorToolbar from "@/components/global/components/tiptap/EditorToolbar";
import { useFormulaEditor } from "@/components/global/components/tiptap/extensions/hooks/useFormulaEditor";
import { mergeParagraphResourceSlotAfterInsert } from "@/components/global/components/tiptap/extensions/paragraph-resource-meta/merge-paragraph-resource-slots";
import { resolvePastedSlotsToResources } from "@/components/global/components/tiptap/extensions/paragraph-resource-meta/resource-slots-clipboard";
import type { ResourceOptionsForPruneRef } from "@/components/global/components/tiptap/extensions/paragraph-resource-meta/resource-slots-prune";
import type {
	ReactionResourceOption,
	SuggestionState,
} from "@/components/global/components/tiptap/extensions/suggestion/suggestion-types";
import { filterResourcesByQuery } from "@/components/global/components/tiptap/extensions/suggestion/suggestion-filter";
import { detectCurrentFormat } from "@/components/global/components/tiptap/helpers";

import { SuggestionsMenu } from "./SuggestionsMenu";

function dedupeReactionResourceOptions(options: ReactionResourceOption[]): ReactionResourceOption[] {
	const byKey = new Map<string, ReactionResourceOption>();
	for (const o of options) {
		byKey.set(`${o.resourceType}:${o.value}`, o);
	}
	return [...byKey.values()];
}

function mergeResourceOptionsForPrune(
	base: ReactionResourceOption[],
	picked: ReactionResourceOption[],
): ReactionResourceOption[] {
	return dedupeReactionResourceOptions([...base, ...picked]);
}

function registerPickedResource(
	resource: ReactionResourceOption,
	baseOptions: ReactionResourceOption[],
	pickedRef: { current: ReactionResourceOption[] },
	effectiveRef: ResourceOptionsForPruneRef,
) {
	pickedRef.current = dedupeReactionResourceOptions([...pickedRef.current, resource]);
	effectiveRef.current = mergeResourceOptionsForPrune(baseOptions, pickedRef.current);
}

export interface ReactionTextAreaHandle {
  insertAtCursor: (resource: ReactionResourceOption) => void;
}

interface ReactionTextAreaProps {
  value?: string;
  onChange?: (html: string) => void;
  availableResources?: ReactionResourceOption[];
}

export const ReactionTextArea = forwardRef<ReactionTextAreaHandle, ReactionTextAreaProps>(
	function ReactionTextArea({value, onChange, availableResources}, ref) {
  
		// holds the current state of the suggestion popup
		const [suggestionState, setSuggestionState] = useState<SuggestionState | null>(null);

		// what item is currently highlighted in the suggestion popup
		const [highlightedIndex, setHighlightedIndex] = useState(0);
  
		// suggestion popup is positioned relative to the container
		const containerRef = useRef<HTMLDivElement | null>(null);

		// mutable state for the suggestion popup
		const menuStateRef = useRef({
			suggestion: null as SuggestionState | null,
			filteredResources: [] as ReactionResourceOption[],
			highlightedIndex: 0,
		});

		// mutable state for the suggestion popup
		const insertResourceRef = useRef<((resource: ReactionResourceOption) => void) | null>(null);

		// we'd retrieve real resources some day
		const resources = useMemo(() => (availableResources?.length ? availableResources : SAMPLE_RESOURCES), [availableResources]);
		const pickedLookupRef = useRef<ReactionResourceOption[]>([]);
		const resourceOptionsRef = useRef(resources);
		useEffect(() => {
			resourceOptionsRef.current = mergeResourceOptionsForPrune(resources, pickedLookupRef.current);
		}, [resources]);

		const filteredResources = useMemo(
			() =>
				suggestionState
					? filterResourcesByQuery(resources, suggestionState.query)
					: [],
			[resources, suggestionState],
		);

		// reset highlights when there are no suggestions
		useEffect(() => {
			if (!filteredResources.length && highlightedIndex !== 0) {
				setHighlightedIndex(0);
				return;
			}

			const maxIndex = filteredResources.length - 1;
			if (filteredResources.length > 0 && highlightedIndex > maxIndex) {
				setHighlightedIndex(maxIndex);
			}
		}, [filteredResources.length, highlightedIndex]);

		useEffect(() => {
			if (!suggestionState) {
				setHighlightedIndex(0);
			}
		}, [suggestionState]);

		// needed for proper positioning of the suggestion popup - returns bounding box of the outer Box
		const getContainerRect = useCallback(() => {
			return containerRef.current?.getBoundingClientRect() ?? null;
		}, []);

		const handleSuggestionKeyDown = useCallback(
			(_view: EditorView, event: KeyboardEvent) => {
				const { suggestion, filteredResources, highlightedIndex } = menuStateRef.current;

				if (!suggestion) {
					return false;
				}

				if (event.key === "Escape") {
					event.preventDefault();
					setSuggestionState(null);
					return true;
				}

				if (!filteredResources.length) {
					return false;
				}

				if (event.key === "ArrowDown") {
					event.preventDefault();
					const total = filteredResources.length;
					setHighlightedIndex((prev) => (prev + 1) % total);
					return true;
				}

				if (event.key === "ArrowUp") {
					event.preventDefault();
					const total = filteredResources.length;
					setHighlightedIndex((prev) => (prev - 1 + total) % total);
					return true;
				}

				if (event.key === "Enter" || event.key === "Tab") {
					event.preventDefault();
					const resource = filteredResources[highlightedIndex];
					if (resource) {
						insertResourceRef.current?.(resource);
					}
					return true;
				}

				return false;
			},
			[]
		);

		const handlePastedResourceSlots = useCallback(
			(slots: string[]) => {
				const resolved = resolvePastedSlotsToResources(slots, resources);
				for (const resource of resolved) {
					registerPickedResource(resource, resources, pickedLookupRef, resourceOptionsRef);
				}
			},
			[resources],
		);

		// initialize tiptap editor
		const editor = useFormulaEditor({
			value,
			onChange,
			onSuggestionUpdate: setSuggestionState,
			onSuggestionKeyDown: handleSuggestionKeyDown,
			getContainerRect,
			resourceOptionsRef,
			preserveResourceSlotsOnClipboard: true,
			onPastedResourceSlots: handlePastedResourceSlots,
		});

		const [currentFormat, setCurrentFormat] = useState("normal");

		useEffect(() => {
			if (!editor) return;
			const handleSelectionUpdate = () => {
				setCurrentFormat(detectCurrentFormat(editor));
			};
			editor.on("selectionUpdate", handleSelectionUpdate);
			editor.on("transaction", handleSelectionUpdate);
			return () => {
				editor.off("selectionUpdate", handleSelectionUpdate);
				editor.off("transaction", handleSelectionUpdate);
			};
		}, [editor]);

		const handleContainerMouseDown = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				if (!editor) return;

				const target = event.target as HTMLElement;
				if (target?.closest(".ProseMirror") || target?.closest(".tiptap-toolbar")) {
					return;
				}

				event.preventDefault();
				editor.chain().focus().run();
			},
			[editor]
		);

		// handles inserting the actual resource when it's been selected via the suggestion popup
		const insertResource = useCallback(
			(resource: ReactionResourceOption) => {
				if (!editor || !suggestionState) {
					return;
				}

				registerPickedResource(resource, resources, pickedLookupRef, resourceOptionsRef);

				editor
					.chain()
					.focus()
					.insertContentAt(suggestionState.range, `{${resource.label}} `)
					.command(mergeParagraphResourceSlotAfterInsert(resource, resource.label))
					.run();

				setSuggestionState(null);
			},
			[editor, resources, suggestionState]
		);

		// inserts a resource at the current cursor position (used by the Insert Resource dropdown)
		const insertAtCursor = useCallback(
			(resource: ReactionResourceOption) => {
				if (!editor) {
					return;
				}
				registerPickedResource(resource, resources, pickedLookupRef, resourceOptionsRef);
				editor
					.chain()
					.focus()
					.insertContent(`{${resource.label}} `)
					.command(mergeParagraphResourceSlotAfterInsert(resource, resource.label))
					.run();
			},
			[editor, resources]
		);

		useImperativeHandle(ref, () => ({ insertAtCursor }), [insertAtCursor]);

		// keyboard handlers always sees latest state
		menuStateRef.current = { suggestion: suggestionState, filteredResources, highlightedIndex };
		insertResourceRef.current = insertResource;

		const showSuggestions = Boolean(suggestionState);

		return (
			<Box
				ref={containerRef}
				onMouseDown={handleContainerMouseDown}
				sx={{
					position: "relative",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					padding: 2,
					backgroundColor: "background.paper",
					minHeight: 160,
				}}
			>
				{editor && (
					<>
						<EditorToolbar
							editor={editor}
							currentFormat={currentFormat}
							setCurrentFormat={setCurrentFormat}
							onLinkPopoverOpen={() => setSuggestionState(null)}
						/>
						<EditorContent
							editor={editor}
							className="tiptap-content-container reaction-text-area-editor"
							style={{ minHeight: 140 }}
						/>
					</>
				)}

				{showSuggestions && suggestionState && (
					<SuggestionsMenu
						resources={filteredResources}
						highlightedIndex={highlightedIndex}
						position={{
							top: (containerRef.current?.getBoundingClientRect().top ?? 0) + suggestionState.position.top,
							left: (containerRef.current?.getBoundingClientRect().left ?? 0) + suggestionState.position.left,
						}}
						onSelect={insertResource}
					/>
				)}
			</Box>
		);
	});

export default ReactionTextArea;
